import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchReservations } from "@/api/reservations";
import { useSelector } from "react-redux";
import { formatPhone } from "@/utils/string";

function getPhoneSuffix(phone: string) {
  const digits = String(phone || "").replace(/-/g, "");
  return digits.slice(-4);
}

function normalizePhone(phone: string) {
  return String(phone || "").replace(/-/g, "");
}

function getSearchParams(searchQuery: string) {
  const trimmed = searchQuery.trim();
  if (!trimmed) {
    return {
      searchWord: "",
      searchPhone: "",
    };
  }

  const normalized = trimmed.replace(/-/g, "");
  const hasDigit = /\d/.test(normalized);
  const hasKoreanOrEnglish = /[a-zA-Z가-힣]/.test(trimmed);

  if (hasDigit && !hasKoreanOrEnglish) {
    return {
      searchWord: "",
      searchPhone: normalized,
    };
  }

  return {
    searchWord: trimmed,
    searchPhone: "",
  };
}

type BookingStatus = "confirmed" | "cancelled";
type FilterStatus = "all" | BookingStatus;
type SortOrder = "newest" | "oldest" | "date_asc" | "date_desc";

type ReservationPreviewSlot = {
  openSlotId: number;
  mappingStatus: "BOOKED" | "CANCELED";
  shopId: number;
  slotDate: string;
  roomId: number;
  startMinuteOfDay: number;
  endMinuteOfDay: number;
  time: string;
  endTime: string;
  slotStatus: "OPEN" | "CLOSED" | "BOOKED";
};

type ReservationListItem = {
  id: number;
  reservationCode: string;
  bookingPlannerId: number;
  customerName: string;
  customerPhone: string;
  reservationStatus: "BOOKED" | "CANCELED";
  reservedTime: string;
  canceledTime: string | null;
  cancelReason: string | null;
  memo: string | null;
  slotCount: number;
  shopCount: number;
  firstSlotDate: string | null;
  lastSlotDate: string | null;
  previewSlots: ReservationPreviewSlot[];
};

type BookingCardItem = {
  id: number;
  code: string;
  customerName: string;
  customerPhone: string;
  status: BookingStatus;
  createdAt: string;
  date: string;
  slotCount: number;
  shopCount: number;
  cancelReason: string | null;
  memo: string | null;
  previewSlots: ReservationPreviewSlot[];
};

type ShopMapItem = {
  id: number;
  shopName: string;
  [key: string]: any;
};

const STATUS_LABEL: Record<BookingStatus, string> = {
  confirmed: "예약완료",
  cancelled: "취소",
};

const STATUS_STYLE: Record<BookingStatus, { bg: string; text: string }> = {
  confirmed: { bg: "#EFF6FF", text: "#2563EB" },
  cancelled: { bg: "#FEF2F2", text: "#DC2626" },
};

const SORT_OPTIONS: { value: SortOrder; label: string }[] = [
  { value: "newest", label: "최근 생성순" },
  { value: "oldest", label: "오래된 생성순" },
  { value: "date_asc", label: "예약일 빠른순" },
  { value: "date_desc", label: "예약일 늦은순" },
];

const PAGE_SIZE = 10;
const DAY_NAMES = ["일", "월", "화", "수", "목", "금", "토"];

function formatDateShort(dateStr: string) {
  const date = new Date(dateStr);
  return `${date.getMonth() + 1}월 ${date.getDate()}일 (${DAY_NAMES[date.getDay()]})`;
}

function formatDateFull(dateStr: string) {
  const date = new Date(dateStr);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")} (${DAY_NAMES[date.getDay()]})`;
}

function normalizeReservationToCard(item: ReservationListItem): BookingCardItem {
  return {
    id: Number(item.id),
    code: item.reservationCode,
    customerName: item.customerName,
    customerPhone: item.customerPhone,
    status: item.reservationStatus === "CANCELED" ? "cancelled" : "confirmed",
    createdAt: item.reservedTime,
    date: item.firstSlotDate || item.reservedTime.slice(0, 10),
    slotCount: Number(item.slotCount || 0),
    shopCount: Number(item.shopCount || 0),
    cancelReason: item.cancelReason || null,
    memo: item.memo || null,
    previewSlots: item.previewSlots || [],
  };
}

function matchesSearch(item: BookingCardItem, query: string) {
  const q = query.trim().replace(/-/g, "");
  if (!q) return true;

  return (
    item.customerName.includes(query.trim()) ||
    normalizePhone(item.customerPhone).includes(q)
  );
}

function SortDropdown({
  sortOrder,
  setSortOrder,
}: {
  sortOrder: SortOrder;
  setSortOrder: (value: SortOrder) => void;
}) {
  const [open, setOpen] = useState(false);

  const currentSortLabel =
    SORT_OPTIONS.find((o) => o.value === sortOrder)?.label ?? "정렬";

  return (
    <div className="relative flex-shrink-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 bg-white rounded-xl px-3 py-2.5 cursor-pointer whitespace-nowrap"
        style={{ border: "1px solid #E5E7EB", color: "#374151" }}
      >
        <i className="ri-sort-desc text-sm" style={{ color: "#9CA3AF" }}></i>
        <span className="text-xs font-medium">{currentSortLabel}</span>
        <i
          className={`ri-arrow-down-s-line text-sm transition-transform ${
            open ? "rotate-180" : ""
          }`}
          style={{ color: "#9CA3AF" }}
        ></i>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)}></div>
          <div
            className="absolute right-0 top-full mt-1 bg-white rounded-xl py-1 z-20 min-w-[148px]"
            style={{
              border: "1px solid #E5E7EB",
              boxShadow: "0 4px 16px rgba(0,0,0,0.07)",
            }}
          >
            {SORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  setSortOrder(opt.value);
                  setOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-xs hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                style={{
                  color: sortOrder === opt.value ? "#2563EB" : "#374151",
                  fontWeight: sortOrder === opt.value ? 600 : 400,
                }}
              >
                {sortOrder === opt.value && (
                  <i className="ri-check-line text-xs" style={{ color: "#2563EB" }}></i>
                )}
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatusTabs({
  value,
  onChange,
  counts,
}: {
  value: FilterStatus;
  onChange: (value: FilterStatus) => void;
  counts?: { all: number; confirmed: number; cancelled: number };
}) {
  const tabs: { value: FilterStatus; label: string; count?: number }[] = [
    { value: "all", label: "전체", count: counts?.all },
    { value: "confirmed", label: "예약완료", count: counts?.confirmed },
    { value: "cancelled", label: "취소", count: counts?.cancelled },
  ];

  return (
    <div
      className="flex items-center gap-0.5 bg-white rounded-xl p-1"
      style={{ border: "1px solid #E5E7EB" }}
    >
      {tabs.map((tab) => {
        const active = value === tab.value;

        return (
          <button
            key={tab.value}
            onClick={() => onChange(tab.value)}
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            style={
              active
                ? { backgroundColor: "#2563EB", color: "#FFFFFF" }
                : { color: "#9CA3AF" }
            }
          >
            <span>{tab.label}</span>
            {typeof tab.count === "number" && (
              <span
                className="tabular-nums"
                style={{
                  color: active ? "#FFFFFF" : "#9CA3AF",
                  opacity: active ? 0.95 : 1,
                }}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

function MobileBookingCard({
  booking,
  shopMap,
}: {
  booking: BookingCardItem;
  shopMap: Record<number, ShopMapItem>;
}) {
  const navigate = useNavigate();
  const statusStyle = STATUS_STYLE[booking.status];

  return (
    <button
      onClick={() => navigate(`/reservations/${booking.code}`)}
      className="w-full bg-white rounded-xl p-4 text-left active:scale-[0.99] transition-transform cursor-pointer"
      style={{ border: "1px solid #EBEBEB" }}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="min-w-0 flex items-center gap-2">
          <span className="text-sm font-semibold truncate" style={{ color: "#111827" }}>
            {booking.customerName}
          </span>
          <span className="text-xs flex-shrink-0" style={{ color: "#9CA3AF" }}>
            ({getPhoneSuffix(booking.customerPhone)})
          </span>
        </div>

        <span
          className="text-[11px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap"
          style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
        >
          {STATUS_LABEL[booking.status]}
        </span>
      </div>

      <p className="text-xs mb-2" style={{ color: "#6B7280" }}>
        {formatDateShort(booking.date)} · {booking.shopCount || booking.previewSlots.length}개 업체
      </p>

      <div className="space-y-1">
        {booking.previewSlots.map((slot, idx) => (
          <div key={`${slot.openSlotId}_${idx}`} className="flex items-center gap-2">
            <span
              className="text-xs font-semibold tabular-nums w-9 flex-shrink-0"
              style={{ color: "#2563EB" }}
            >
              {slot.time}
            </span>
            <span className="text-xs truncate" style={{ color: "#374151" }}>
              {shopMap[slot.shopId]?.shopName || `업체 #${slot.shopId}`}
            </span>
          </div>
        ))}
      </div>

      <div
        className="flex items-center justify-between mt-2.5 pt-2"
        style={{ borderTop: "1px solid #F3F4F6" }}
      >
        <span className="text-xs font-mono" style={{ color: "#D1D5DB" }}>
          {booking.code}
        </span>
        <i className="ri-arrow-right-s-line text-sm" style={{ color: "#D1D5DB" }}></i>
      </div>
    </button>
  );
}

function DesktopTableRow({
  booking,
  shopMap,
  isEven,
}: {
  booking: BookingCardItem;
  shopMap: Record<number, ShopMapItem>;
  isEven: boolean;
}) {
  const navigate = useNavigate();
  const statusStyle = STATUS_STYLE[booking.status];

  const slotsLine = booking.previewSlots
    .map((slot) => `${slot.time} ${shopMap[slot.shopId]?.shopName || `업체 #${slot.shopId}`}`)
    .join(" · ");

  return (
    <tr
      onClick={() => navigate(`/reservations/${booking.code}`)}
      className="cursor-pointer transition-colors group"
      style={{ backgroundColor: isEven ? "#FAFAFA" : "#FFFFFF" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "#F0F4FF";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isEven ? "#FAFAFA" : "#FFFFFF";
      }}
    >
      <td className="px-4 py-3 whitespace-nowrap align-middle">
        <span className="text-sm font-semibold" style={{ color: "#111827" }}>
          {booking.customerName}
        </span>
      </td>

      <td className="px-4 py-3 whitespace-nowrap align-middle">
        <span className="text-sm tabular-nums" style={{ color: "#6B7280" }}>
          {formatPhone(booking.customerPhone)}
        </span>
      </td>

      <td className="px-4 py-3 align-middle" style={{ minWidth: "420px" }}>
        <p className="text-xs font-medium mb-0.5" style={{ color: "#374151" }}>
          {formatDateFull(booking.date)} · {booking.shopCount || booking.previewSlots.length}개 업체
        </p>
        <p className="text-xs truncate" style={{ color: "#9CA3AF", maxWidth: "100%" }}>
          {slotsLine}
        </p>
      </td>

      <td className="px-4 py-3 whitespace-nowrap align-middle">
        <span
          className="text-xs font-medium px-2 py-0.5 rounded-full"
          style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
        >
          {STATUS_LABEL[booking.status]}
        </span>
      </td>

      <td className="px-4 py-3 whitespace-nowrap text-right align-middle">
        <i
          className="ri-arrow-right-s-line text-base"
          style={{ color: "#D1D5DB" }}
        ></i>
      </td>
    </tr>
  );
}

function MobileSkeletonCard() {
  return (
    <div
      className="w-full bg-white rounded-xl p-4 animate-pulse"
      style={{ border: "1px solid #EBEBEB" }}
    >
      <div className="flex justify-between mb-2">
        <div className="h-4 w-20 rounded" style={{ backgroundColor: "#F3F4F6" }}></div>
        <div className="h-4 w-14 rounded-full" style={{ backgroundColor: "#F3F4F6" }}></div>
      </div>
      <div className="h-3 w-32 rounded mb-2" style={{ backgroundColor: "#F3F4F6" }}></div>
      <div className="space-y-1.5">
        <div className="h-3 w-full rounded" style={{ backgroundColor: "#F3F4F6" }}></div>
        <div className="h-3 w-4/5 rounded" style={{ backgroundColor: "#F3F4F6" }}></div>
      </div>
    </div>
  );
}

function DesktopSkeletonTable() {
  return (
    <div
      className="bg-white rounded-xl overflow-hidden"
      style={{ border: "1px solid #E5E7EB" }}
    >
      <div
        className="grid grid-cols-[160px_180px_minmax(320px,1fr)_120px_70px] items-center px-4 py-3"
        style={{ backgroundColor: "#FAFAFA", borderBottom: "1px solid #F3F4F6" }}
      >
        {["고객명", "전화번호", "일정 / 업체", "상태", "액션"].map((label) => (
          <div
            key={label}
            className="text-xs font-semibold"
            style={{ color: "#9CA3AF" }}
          >
            {label}
          </div>
        ))}
      </div>

      {[0, 1, 2, 3].map((idx) => (
        <div
          key={idx}
          className="grid grid-cols-[160px_180px_minmax(320px,1fr)_120px_70px] items-center px-4 py-3 animate-pulse"
          style={{
            backgroundColor: idx % 2 === 1 ? "#FAFAFA" : "#FFFFFF",
            borderBottom: idx !== 3 ? "1px solid #F9FAFB" : "none",
          }}
        >
          <div className="h-4 w-20 rounded" style={{ backgroundColor: "#F3F4F6" }}></div>
          <div className="h-4 w-28 rounded" style={{ backgroundColor: "#F3F4F6" }}></div>
          <div>
            <div className="h-3 w-40 rounded mb-2" style={{ backgroundColor: "#F3F4F6" }}></div>
            <div className="h-3 w-full rounded" style={{ backgroundColor: "#F3F4F6" }}></div>
          </div>
          <div className="h-5 w-14 rounded-full" style={{ backgroundColor: "#F3F4F6" }}></div>
          <div className="flex justify-end">
            <div className="h-4 w-4 rounded" style={{ backgroundColor: "#F3F4F6" }}></div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 px-5">
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-3"
        style={{ backgroundColor: "#F3F4F6" }}
      >
        <i className="ri-file-list-3-line text-xl" style={{ color: "#D1D5DB" }}></i>
      </div>
      <p className="text-sm font-semibold mb-1" style={{ color: "#374151" }}>
        {hasFilter ? "검색 결과가 없습니다" : "아직 예약 내역이 없습니다"}
      </p>
      <p className="text-xs text-center leading-relaxed" style={{ color: "#9CA3AF" }}>
        {hasFilter
          ? "다른 검색어나 필터를 사용해보세요"
          : "조합 찾기를 통해 첫 예약을 생성해보세요"}
      </p>
    </div>
  );
}

export default function BookingsPage() {
  const navigate = useNavigate();
  const shops = useSelector((state: any) => state.shop?.shops || []);

  const shopMap = useMemo(() => {
    return (shops || []).reduce((acc: Record<number, ShopMapItem>, shop: ShopMapItem) => {
      acc[Number(shop.id)] = shop;
      return acc;
    }, {});
  }, [shops]);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");

  const [items, setItems] = useState<BookingCardItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const [page, setPage] = useState(1);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loaderRef = useRef<HTMLDivElement>(null);

  const hasMoreServerData = items.length < totalCount;

  const fetchPage = useCallback(
    async ({
      nextPage,
      append,
      query,
    }: {
      nextPage: number;
      append: boolean;
      query: string;
    }) => {
      const { searchWord, searchPhone } = getSearchParams(query);

      const response = await fetchReservations(
        nextPage,
        PAGE_SIZE,
        searchWord,
        searchPhone
      );

      const payload = response?.data || response || {};
      const reservations = Array.isArray(payload?.reservations)
        ? payload.reservations
        : [];
      const normalized = reservations.map(normalizeReservationToCard);

      setTotalCount(Number(payload?.count || 0));

      setItems((prev) => {
        if (!append) {
          return normalized;
        }

        const merged = [...prev, ...normalized];
        const dedupedMap = new Map<string, BookingCardItem>();

        merged.forEach((item) => {
          dedupedMap.set(item.code, item);
        });

        return Array.from(dedupedMap.values());
      });
    },
    []
  );

  useEffect(() => {
    let mounted = true;

    async function loadInitial() {
      try {
        setIsInitialLoading(true);
        setPage(1);

        await fetchPage({
          nextPage: 1,
          append: false,
          query: searchQuery,
        });
      } finally {
        if (mounted) {
          setIsInitialLoading(false);
        }
      }
    }

    loadInitial();

    return () => {
      mounted = false;
    };
  }, [fetchPage, searchQuery]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || isInitialLoading || !hasMoreServerData) return;

    try {
      setIsLoadingMore(true);
      const nextPage = page + 1;

      await fetchPage({
        nextPage,
        append: true,
        query: searchQuery,
      });

      setPage(nextPage);
    } finally {
      setIsLoadingMore(false);
    }
  }, [fetchPage, hasMoreServerData, isInitialLoading, isLoadingMore, page, searchQuery]);

  useEffect(() => {
    const el = loaderRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [loadMore]);

  const filtered = useMemo(() => {
    let result = [...items];

    result = result.filter((item) => matchesSearch(item, searchQuery));

    if (filterStatus !== "all") {
      result = result.filter((item) => item.status === filterStatus);
    }

    result.sort((a, b) => {
      if (sortOrder === "newest") return b.createdAt.localeCompare(a.createdAt);
      if (sortOrder === "oldest") return a.createdAt.localeCompare(b.createdAt);

      const aDateKey = `${a.date} ${a.previewSlots[0]?.time ?? ""}`;
      const bDateKey = `${b.date} ${b.previewSlots[0]?.time ?? ""}`;

      if (sortOrder === "date_asc") {
        return aDateKey.localeCompare(bDateKey);
      }
      if (sortOrder === "date_desc") {
        return bDateKey.localeCompare(aDateKey);
      }

      return 0;
    });

    return result;
  }, [items, searchQuery, filterStatus, sortOrder]);

  const hasFilter =
    searchQuery.trim() !== "" || filterStatus !== "all";

  const confirmedCount = useMemo(
    () => items.filter((item) => item.status === "confirmed").length,
    [items]
  );

  const cancelledCount = useMemo(
    () => items.filter((item) => item.status === "cancelled").length,
    [items]
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F5F6F8" }}>
      <div
        className="bg-white border-b px-4 md:px-6 py-3.5 flex items-center gap-3"
        style={{ borderColor: "#EBEBEB" }}
      >
        <button
          onClick={() => navigate("/")}
          className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer flex-shrink-0"
          style={{ color: "#374151" }}
        >
          <i className="ri-arrow-left-line text-lg"></i>
        </button>

        <div className="flex-1 min-w-0">
          <h1 className="text-sm md:text-base font-bold" style={{ color: "#111827" }}>
            예약 내역
          </h1>
          <p className="hidden md:block text-xs mt-0.5" style={{ color: "#9CA3AF" }}>
            내가 생성한 예약과 상태를 확인할 수 있습니다
          </p>
        </div>
      </div>

      <div className="md:hidden">
        <div className="px-4 pt-3 pb-2">
          <div
            className="flex items-center gap-2 bg-white rounded-xl px-3 py-2.5"
            style={{ border: "1px solid #E5E7EB" }}
          >
            <i className="ri-search-line text-sm flex-shrink-0" style={{ color: "#9CA3AF" }}></i>
            <input
              type="text"
              placeholder="고객명 또는 전화번호 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 text-sm bg-transparent outline-none min-w-0"
              style={{ color: "#111827" }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="w-4 h-4 flex items-center justify-center flex-shrink-0 cursor-pointer"
                style={{ color: "#D1D5DB" }}
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            )}
          </div>
        </div>

        <div className="px-4 pb-2 flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <StatusTabs value={filterStatus} onChange={setFilterStatus} />
          </div>
          <SortDropdown sortOrder={sortOrder} setSortOrder={setSortOrder} />
        </div>

        <div className="px-4 pb-2">
          <p className="text-xs" style={{ color: "#9CA3AF" }}>
            총{" "}
            <span className="font-semibold" style={{ color: "#374151" }}>
              {filtered.length}
            </span>
            건
          </p>
        </div>

        <div className="px-4 pb-10">
          {isInitialLoading ? (
            <div className="space-y-2">
              <MobileSkeletonCard />
              <MobileSkeletonCard />
              <MobileSkeletonCard />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState hasFilter={hasFilter} />
          ) : (
            <>
              <div className="space-y-2">
                {filtered.map((booking) => (
                  <MobileBookingCard
                    key={booking.code}
                    booking={booking}
                    shopMap={shopMap}
                  />
                ))}
              </div>

              {hasMoreServerData && (
                <div ref={loaderRef} className="mt-3">
                  {isLoadingMore && (
                    <div className="space-y-2">
                      <MobileSkeletonCard />
                      <MobileSkeletonCard />
                    </div>
                  )}
                </div>
              )}

              {!hasMoreServerData && filtered.length > PAGE_SIZE && (
                <div className="flex items-center justify-center gap-3 mt-5 mb-2">
                  <div className="h-px flex-1" style={{ backgroundColor: "#F3F4F6" }}></div>
                  <p className="text-xs" style={{ color: "#D1D5DB" }}>
                    전체 {filtered.length}건
                  </p>
                  <div className="h-px flex-1" style={{ backgroundColor: "#F3F4F6" }}></div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="hidden md:block px-6 py-4">
        <div
          className="bg-white rounded-xl px-4 py-3 flex items-center justify-between gap-4 mb-3"
          style={{ border: "1px solid #E5E7EB" }}
        >
          <StatusTabs
            value={filterStatus}
            onChange={setFilterStatus}
            counts={{
              all: totalCount || items.length,
              confirmed: confirmedCount,
              cancelled: cancelledCount,
            }}
          />

          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              className="flex items-center gap-2 bg-white rounded-lg px-3 py-2"
              style={{ border: "1px solid #E5E7EB", minWidth: "260px" }}
            >
              <i className="ri-search-line text-sm flex-shrink-0" style={{ color: "#9CA3AF" }}></i>
              <input
                type="text"
                placeholder="고객명 또는 전화번호 검색"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 text-sm bg-transparent outline-none min-w-0"
                style={{ color: "#111827" }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="w-4 h-4 flex items-center justify-center flex-shrink-0 cursor-pointer"
                  style={{ color: "#D1D5DB" }}
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
            </div>

            <SortDropdown sortOrder={sortOrder} setSortOrder={setSortOrder} />
          </div>
        </div>

        <div className="mb-2 px-1">
          <p className="text-xs" style={{ color: "#9CA3AF" }}>
            총{" "}
            <span className="font-semibold" style={{ color: "#374151" }}>
              {filtered.length}
            </span>
            건
          </p>
        </div>

        {isInitialLoading ? (
          <DesktopSkeletonTable />
        ) : filtered.length === 0 ? (
          <div
            className="bg-white rounded-xl"
            style={{ border: "1px solid #E5E7EB" }}
          >
            <EmptyState hasFilter={hasFilter} />
          </div>
        ) : (
          <div
            className="bg-white rounded-xl overflow-hidden"
            style={{ border: "1px solid #E5E7EB" }}
          >
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ borderBottom: "1px solid #F3F4F6" }}>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold whitespace-nowrap"
                    style={{ color: "#9CA3AF", backgroundColor: "#FAFAFA" }}
                  >
                    고객명
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold whitespace-nowrap"
                    style={{ color: "#9CA3AF", backgroundColor: "#FAFAFA" }}
                  >
                    전화번호
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold"
                    style={{ color: "#9CA3AF", backgroundColor: "#FAFAFA" }}
                  >
                    일정 / 업체
                  </th>
                  <th
                    className="px-4 py-2.5 text-left text-xs font-semibold whitespace-nowrap"
                    style={{ color: "#9CA3AF", backgroundColor: "#FAFAFA" }}
                  >
                    상태
                  </th>
                  <th
                    className="px-4 py-2.5 text-right text-xs font-semibold whitespace-nowrap"
                    style={{ color: "#9CA3AF", backgroundColor: "#FAFAFA" }}
                  >
                    액션
                  </th>
                </tr>
              </thead>

              <tbody>
                {filtered.map((booking, idx) => (
                  <DesktopTableRow
                    key={booking.code}
                    booking={booking}
                    shopMap={shopMap}
                    isEven={idx % 2 === 1}
                  />
                ))}
              </tbody>
            </table>

            {hasMoreServerData && (
              <div ref={loaderRef} className="px-4 py-3">
                {isLoadingMore && (
                  <div className="text-xs text-center" style={{ color: "#9CA3AF" }}>
                    불러오는 중...
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}